import React, { useState, useEffect, useRef } from 'react';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import {
  getRecordedAudio,
  saveRecordedAudio,
  deleteRecordedAudio,
  hasRecordedAudio,
} from '../utils/audioStorage';
import { playSound } from '../audio';

interface TaleSentenceAudioRecorderProps {
  sentenceKabyle: string;
  sentenceFrench?: string;
  index?: number;
  onAudioUpdated?: () => void;
}

export const TaleSentenceAudioRecorder: React.FC<TaleSentenceAudioRecorderProps> = ({
  sentenceKabyle,
  sentenceFrench,
  index,
  onAudioUpdated,
}) => {
  const normalizedKey = (sentenceKabyle || '').toLowerCase().trim();
  const [existingAudio, setExistingAudio] = useState<string | null>(null);
  const [isPlayingExisting, setIsPlayingExisting] = useState(false);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);

  const {
    isRecording,
    recordingTime,
    audioDataUrl,
    error,
    prepareMicrophone,
    startRecording,
    stopRecording,
    resetRecording,
    playPreview,
    isPlayingPreview,
  } = useVoiceRecorder();

  // Load existing audio on mount or when sentenceKabyle changes
  useEffect(() => {
    const updateExisting = () => {
      if (normalizedKey) {
        setExistingAudio(getRecordedAudio(normalizedKey));
      } else {
        setExistingAudio(null);
      }
    };

    updateExisting();
    resetRecording();
    setSaveSuccessMessage(null);
    setCountdown(null);

    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }
    setIsPlayingExisting(false);

    const handleAudioUpdated = (e: Event) => {
      const customEvent = e as CustomEvent<{ word?: string; all?: boolean }>;
      if (customEvent.detail?.all || customEvent.detail?.word === normalizedKey) {
        updateExisting();
      }
    };

    window.addEventListener('kabyle_audio_updated', handleAudioUpdated);
    return () => {
      window.removeEventListener('kabyle_audio_updated', handleAudioUpdated);
      if (countdownTimerRef.current) clearTimeout(countdownTimerRef.current);
      if (audioPlayerRef.current) audioPlayerRef.current.pause();
    };
  }, [normalizedKey, resetRecording]);

  // Handle countdown before recording
  const triggerRecordingWithCountdown = async () => {
    playSound('click');
    resetRecording();
    try {
      await prepareMicrophone();
      setCountdown(2);
    } catch (err) {
      console.warn('Microphone error on trigger:', err);
    }
  };

  const cancelCountdown = () => {
    if (countdownTimerRef.current) {
      clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdown(null);
    resetRecording();
    playSound('click');
  };

  useEffect(() => {
    if (countdown === null) return;

    if (countdown > 0) {
      playSound('click');
      countdownTimerRef.current = setTimeout(() => {
        setCountdown((prev) => (prev !== null ? prev - 1 : null));
      }, 1000);
      return () => {
        if (countdownTimerRef.current) clearTimeout(countdownTimerRef.current);
      };
    } else if (countdown === 0) {
      playSound('stele');
      setCountdown(null);
      startRecording();
    }
  }, [countdown, startRecording]);

  const handlePlayExisting = () => {
    if (!existingAudio) return;
    if (isPlayingExisting && audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      setIsPlayingExisting(false);
      return;
    }
    setIsPlayingExisting(true);
    const audio = new Audio(existingAudio);
    audioPlayerRef.current = audio;
    audio.onended = () => {
      setIsPlayingExisting(false);
      audioPlayerRef.current = null;
    };
    audio.onerror = () => {
      setIsPlayingExisting(false);
      audioPlayerRef.current = null;
    };
    audio.play().catch(() => {
      setIsPlayingExisting(false);
      audioPlayerRef.current = null;
    });
  };

  const handleSaveTake = async () => {
    if (!audioDataUrl || !normalizedKey) return;
    setExistingAudio(audioDataUrl);
    await saveRecordedAudio(normalizedKey, audioDataUrl);
    resetRecording();
    setSaveSuccessMessage('Phrase enregistrée !');
    setTimeout(() => setSaveSuccessMessage(null), 3000);
    playSound('correct');
    if (onAudioUpdated) onAudioUpdated();
  };

  const handleDeleteExisting = async () => {
    if (!normalizedKey) return;
    await deleteRecordedAudio(normalizedKey);
    setExistingAudio(null);
    resetRecording();
    playSound('click');
    if (onAudioUpdated) onAudioUpdated();
  };

  return (
    <div className="w-full bg-[#fbf5e8]/90 border border-amber-800/25 rounded-xl p-2 sm:p-2.5 shadow-xs space-y-1.5 select-none transition-all">
      {/* Mini Header & Status */}
      <div className="flex items-center justify-between gap-1.5 flex-wrap">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-xs">🎙️</span>
          <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-amber-950 truncate">
            {typeof index === 'number' ? `Phrase ${index + 1}` : 'Enregistrement de la phrase'}
          </span>
          <span className="text-[8px] sm:text-[9px] px-1 py-0.1 bg-amber-200/90 text-amber-950 border border-amber-400/80 rounded font-bold uppercase shrink-0">
            Admin
          </span>
        </div>

        {/* Existing Audio Status Pill */}
        {existingAudio && !isRecording && !audioDataUrl && (
          <span className="text-[9px] sm:text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-900 border border-emerald-400 flex items-center gap-1 shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse"></span>
            <span>Voix personnalisée</span>
          </span>
        )}
        {!existingAudio && !isRecording && !audioDataUrl && (
          <span className="text-[9px] sm:text-[10px] font-bold px-1.5 py-0.5 rounded bg-stone-200/80 text-stone-600 border border-stone-300 shrink-0">
            Non enregistrée
          </span>
        )}
      </div>

      {/* Success Banner */}
      {saveSuccessMessage && (
        <div className="py-1 px-2 bg-emerald-100 border border-emerald-400 text-emerald-950 text-[11px] rounded-lg font-bold flex items-center gap-1.5 animate-in fade-in">
          <span>✅</span>
          <span>{saveSuccessMessage}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="py-1 px-2 bg-red-100 border border-red-400 text-red-950 text-[11px] rounded-lg font-bold flex items-center gap-1.5">
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {/* Active Countdown */}
      {countdown !== null && (
        <div className="py-2 px-2.5 bg-amber-100 border border-amber-500 rounded-lg text-center space-y-1 animate-pulse">
          <div className="text-[10px] font-bold text-amber-950 uppercase tracking-wider">
            Préparez-vous à prononcer la phrase...
          </div>
          <div className="text-xl font-black text-amber-900">{countdown}</div>
          <button
            type="button"
            onClick={cancelCountdown}
            className="text-[10px] font-bold text-red-700 underline hover:text-red-900 cursor-pointer"
          >
            Annuler
          </button>
        </div>
      )}

      {/* Active Recording State */}
      {isRecording && (
        <div className="py-2 px-2.5 bg-red-50 border border-red-500 rounded-lg space-y-1.5">
          <div className="flex items-center justify-between text-[11px] font-bold text-red-900">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-red-600 animate-ping"></span>
              <span className="uppercase tracking-wider">Enregistrement en cours</span>
            </div>
            <span className="font-mono text-xs font-black text-red-700 bg-red-100 px-1.5 py-0.5 rounded border border-red-300">
              {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
            </span>
          </div>

          {/* Animated sound wave bars */}
          <div className="flex items-center justify-center gap-1 h-5">
            {[35, 70, 95, 55, 80, 90, 45, 75, 95, 65, 85, 55].map((h, i) => (
              <div
                key={i}
                className="w-1 bg-red-600 rounded-full animate-pulse"
                style={{
                  height: `${h}%`,
                  animationDelay: `${i * 0.08}s`,
                  animationDuration: '0.6s',
                }}
              />
            ))}
          </div>

          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={stopRecording}
              className="flex-1 py-1 bg-red-700 hover:bg-red-800 text-white font-bold text-[11px] rounded-lg uppercase tracking-wider shadow-xs cursor-pointer flex items-center justify-center gap-1"
            >
              <span>⏹️</span>
              <span>Terminer la Prise</span>
            </button>
            <button
              type="button"
              onClick={resetRecording}
              className="py-1 px-2 bg-stone-200 hover:bg-stone-300 text-stone-800 font-bold text-[11px] rounded-lg uppercase cursor-pointer"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Review New Take */}
      {audioDataUrl && !isRecording && (
        <div className="p-2 bg-emerald-50 border border-emerald-500 rounded-lg space-y-1.5">
          <div className="text-[11px] font-bold text-emerald-950 flex items-center justify-between">
            <span>✨ Extrait capturé</span>
            <span className="text-[9px] text-emerald-800">Prêt à valider</span>
          </div>

          <div className="grid grid-cols-3 gap-1.5">
            <button
              type="button"
              onClick={playPreview}
              className="py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[11px] rounded-lg uppercase tracking-wider flex items-center justify-center gap-1 shadow-xs cursor-pointer"
            >
              <span>{isPlayingPreview ? '⏸️' : '▶️'}</span>
              <span>{isPlayingPreview ? 'Pause' : 'Écouter'}</span>
            </button>

            <button
              type="button"
              onClick={handleSaveTake}
              className="py-1.5 bg-amber-700 hover:bg-amber-800 text-white font-black text-[11px] rounded-lg uppercase tracking-wider flex items-center justify-center gap-1 shadow-xs cursor-pointer border border-amber-600"
            >
              <span>💾</span>
              <span>Sauvegarder</span>
            </button>

            <button
              type="button"
              onClick={resetRecording}
              className="py-1.5 bg-stone-200 hover:bg-stone-300 text-stone-800 font-bold text-[11px] rounded-lg uppercase tracking-wider flex items-center justify-center gap-1 cursor-pointer"
            >
              <span>🔄</span>
              <span>Refaire</span>
            </button>
          </div>
        </div>
      )}

      {/* Idle State: Action Buttons */}
      {!isRecording && countdown === null && !audioDataUrl && (
        <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
          <button
            type="button"
            onClick={triggerRecordingWithCountdown}
            className="flex-1 py-1.5 px-2.5 bg-gradient-to-r from-red-600 to-rose-700 hover:from-red-500 hover:to-rose-600 text-white font-black text-[11px] uppercase tracking-wider rounded-lg shadow-xs flex items-center justify-center gap-1.5 cursor-pointer active:scale-98 transition-all"
          >
            <span>🎙️</span>
            <span>{existingAudio ? 'Réenregistrer la phrase' : 'Enregistrer la phrase'}</span>
          </button>

          {existingAudio && (
            <>
              <button
                type="button"
                onClick={handlePlayExisting}
                className="py-1.5 px-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[11px] uppercase tracking-wider rounded-lg shadow-xs flex items-center justify-center gap-1 cursor-pointer active:scale-98 transition-all"
                title="Écouter la voix enregistrée pour cette phrase"
              >
                <span>{isPlayingExisting ? '⏸️' : '▶️'}</span>
                <span>{isPlayingExisting ? 'Arrêter' : 'Écouter'}</span>
              </button>

              <button
                type="button"
                onClick={handleDeleteExisting}
                className="py-1.5 px-2 bg-rose-100 hover:bg-rose-200 text-rose-900 border border-rose-300 font-bold text-[11px] uppercase tracking-wider rounded-lg shadow-xs flex items-center justify-center cursor-pointer active:scale-98 transition-all"
                title="Supprimer l'enregistrement de cette phrase"
              >
                <span>🗑️</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
